import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  UtensilsCrossed,
  Fuel,
  ShoppingBag,
  Dumbbell,
  Warehouse,
  Umbrella,
  Store,
  Sparkles,
  SprayCan,
  Wind,
  Droplets,
  Footprints,
  ShieldCheck,
  ClipboardCheck,
  MapPin,
  Phone,
  ArrowRight,
  CheckCircle2,
  CalendarClock,
  Repeat,
  ChevronDown,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";

const SITE_URL = "https://deepcleanrr.com";
const CANONICAL = `${SITE_URL}/services/professional-restroom-cleaning`;

const HERO_IMG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_deep-hygiene/artifacts/zint4arm_Restroom%20Sings.png.webp";
const INFOGRAPHIC_IMG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_deep-hygiene/artifacts/26bzzw0e_Deep%20Clean%20Infographic.png";

const WHO_WE_SERVE = [
  {
    icon: UtensilsCrossed,
    title: "Restaurants & Bars",
    desc: "Wilmington Historic District, Riverwalk, and the Cargo District — where a spotless restroom protects both your food-safety reputation and repeat business.",
  },
  {
    icon: Fuel,
    title: "Gas Stations & Convenience Stores",
    desc: "High-traffic locations along Market Street, Oleander Drive, College Road, and South College Road serving locals and beach-bound travelers.",
  },
  {
    icon: ShoppingBag,
    title: "Retail Centers & Shopping Plazas",
    desc: "Mayfaire Town Center, The Pointe at Barclay, Independence Mall — shopper-facing restrooms that reflect on every tenant.",
  },
  {
    icon: Building2,
    title: "Office Buildings",
    desc: "Downtown Wilmington and the Landfall / Military Cutoff corridor — professional facilities that need consistent, quiet, thorough sanitation.",
  },
  {
    icon: Dumbbell,
    title: "Gyms & Fitness Studios",
    desc: "Monkey Junction, Ogden, and Porters Neck — locker-room grade sanitation to control sweat, humidity, and odor at the source.",
  },
  {
    icon: Store,
    title: "Quick-Serve & Neighborhood Retail",
    desc: "Leland, Hampstead, and Castle Hayne — commercial restrooms in growing suburban Cape Fear communities.",
  },
  {
    icon: Umbrella,
    title: "Beach-Area Businesses",
    desc: "Wrightsville Beach, Carolina Beach, and Kure Beach — seasonal spikes, sandy floors, and salt-air corrosion require a specialist.",
  },
  {
    icon: Warehouse,
    title: "Warehouses & Industrial",
    desc: "Break-room and shop restrooms across New Hanover, Pender, and Brunswick counties — kept safe, compliant, and code-ready.",
  },
];

const SEVEN_LAYERS = [
  {
    icon: ShieldCheck,
    title: "High-Touch Surface Disinfection",
    desc: "Door handles, faucet handles, stall locks, and dispensers — sanitized with hospital-grade product.",
  },
  {
    icon: SprayCan,
    title: "Deep Foam Cleaning",
    desc: "Professional foam application breaks down grease, soap scum, and built-up grime standard mopping misses.",
  },
  {
    icon: Sparkles,
    title: "Fixture Detailing",
    desc: "Toilets, urinals, and sinks are scrubbed and sanitized — not just rinsed.",
  },
  {
    icon: Wind,
    title: "Odor Elimination",
    desc: "Odors treated at the source — no air fresheners masking the smell for a few hours.",
  },
  {
    icon: Footprints,
    title: "Floor & Baseboard Sanitation",
    desc: "The areas most cleaning crews skip. We catch the corners, edges, and grout.",
  },
  {
    icon: Droplets,
    title: "Restocking & Supply Checks",
    desc: "Soap, paper towels, and toilet paper checked and replenished every visit.",
  },
  {
    icon: ClipboardCheck,
    title: "Final Inspection",
    desc: "Every restroom is inspected before we leave to meet our standard — every time.",
  },
];

const SERVICE_AREAS = [
  "Wilmington (Downtown)",
  "Midtown Wilmington",
  "Landfall",
  "Wrightsville Beach",
  "Carolina Beach",
  "Kure Beach",
  "Leland",
  "Hampstead",
  "Monkey Junction",
  "Porters Neck",
  "Castle Hayne",
  "Ogden / Market St. Corridor",
];

const FAQS = [
  {
    q: "Why does restroom cleanliness matter so much for Wilmington businesses?",
    a: "Restroom cleanliness is one of the top factors customers use to judge the trustworthiness of a business. Wilmington-area restaurants, gas stations, retail, and gyms all live and die on reviews — and a neglected restroom drives bad reviews faster than almost anything else.",
  },
  {
    q: "Do you offer both one-time deep cleans and recurring service?",
    a: "Yes. Many clients start with a one-time deep clean (grand opening, inspection prep, or resetting a neglected restroom) and then move to a weekly, bi-weekly, or monthly recurring plan so their staff never has to worry about it.",
  },
  {
    q: "What areas of the Cape Fear region do you cover?",
    a: "We cover Wilmington (downtown, Midtown, Landfall), Wrightsville Beach, Carolina Beach, Kure Beach, Leland, Hampstead, Monkey Junction, Porters Neck, Castle Hayne, and the Market Street / Ogden corridor — all within a 20-minute radius of Wilmington.",
  },
  {
    q: "How is your pricing structured?",
    a: "Per-unit. Restrooms are priced based on the number of restrooms and fixtures (toilets, urinals) at your location — so you only pay for what you need. Contact us for a free per-unit quote.",
  },
  {
    q: "Are you local, or a national franchise?",
    a: "Deep Clean Restroom Services is locally owned and based in the Wilmington area. We're not juggling hundreds of accounts across the state — we know the area, we show up on time, and we treat every restroom like it's a reflection of our own name.",
  },
];

const Faq = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`pro-faq-${idx}`} className="border-b border-slate-200 py-5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
        data-testid={`pro-faq-toggle-${idx}`}
      >
        <span className="font-heading text-lg font-bold text-slate-900 md:text-xl">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={2}
          className={`shrink-0 text-blue-600 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="mt-3 font-body text-base font-medium leading-relaxed text-slate-600">
          {a}
        </p>
      )}
    </div>
  );
};

const ProfessionalRestroomCleaningPage = () => {
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Commercial Restroom Cleaning & Janitorial",
    provider: {
      "@type": "LocalBusiness",
      name: "Deep Clean Restroom Services",
      telephone: "+19102369971",
      email: "deepcleanrr@gmail.com",
      url: SITE_URL,
    },
    areaServed: SERVICE_AREAS.map((a) => ({ "@type": "Place", name: a })),
    url: CANONICAL,
    description:
      "Professional commercial restroom cleaning and janitorial services for Wilmington, NC businesses — restaurants, gas stations, retail, gyms, offices, and beach-area facilities across the Cape Fear region.",
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <main data-testid="pro-service-page" className="bg-white">
      <SEO
        title="Why Professional Restroom Cleaning Matters | Wilmington, NC"
        description="Wilmington, NC's specialist in commercial restroom cleaning. Restaurants, gas stations, retail, gyms, offices, and beach-area businesses — served across Wilmington, Wrightsville Beach, Carolina Beach, Leland, Hampstead, and the Cape Fear region. 7 Layers of Clean. Local. Fully insured."
        canonical={CANONICAL}
        image={HERO_IMG}
        type="website"
        jsonLd={[serviceJsonLd, faqJsonLd]}
        keywords={[
          "professional restroom cleaning Wilmington NC",
          "commercial restroom cleaning Wilmington",
          "commercial janitorial services Wilmington NC",
          "Cape Fear commercial cleaning",
          "restaurant restroom cleaning Wilmington",
          "gas station restroom cleaning Wilmington",
          "retail restroom cleaning Wilmington",
          "gym restroom cleaning Wilmington",
          "office restroom cleaning Wilmington",
          "Wrightsville Beach restroom cleaning",
          "Carolina Beach restroom cleaning",
          "Kure Beach restroom cleaning",
          "Leland restroom cleaning",
          "Hampstead restroom cleaning",
          "Monkey Junction cleaning services",
          "Porters Neck restroom cleaning",
          "Castle Hayne cleaning",
          "7 layers of clean Wilmington",
          "recurring janitorial contract Wilmington",
          "one-time deep restroom clean Wilmington",
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-slate-950 pt-24">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          aria-hidden="true"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div
          className="absolute inset-0 -z-10"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,9,19,0.86) 0%, rgba(5,9,19,0.60) 30%, rgba(5,9,19,0.45) 100%), linear-gradient(90deg, rgba(5,9,19,0.82) 0%, rgba(5,9,19,0.42) 60%, rgba(5,9,19,0.10) 100%), radial-gradient(60% 50% at 85% 20%, rgba(6,182,212,0.22), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-16 md:px-12 md:pb-32 md:pt-24">
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
            <MapPin size={14} strokeWidth={2} className="text-cyan-300" />
            Wilmington, NC · Cape Fear Region
          </p>
          <h1
            data-testid="pro-hero-heading"
            className="max-w-5xl font-heading text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-8xl"
          >
            Why professional
            <br />
            <span className="text-cyan-300">restroom cleaning</span>
            <br />
            matters here.
          </h1>
          <p className="mt-8 max-w-3xl font-body text-base font-medium leading-relaxed text-slate-200 md:text-lg">
            Your restrooms say more about your business than almost anything
            else. Deep Clean Restroom Services is Wilmington's specialist in
            commercial restroom sanitation — built for restaurants, retail,
            gyms, offices, gas stations, and beach-area facilities across the
            Cape Fear region.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="pro-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Request a Free Quote
              <ArrowRight
                size={18}
                strokeWidth={2.2}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="pro-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              Call 910-236-9971
            </a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="border-b border-slate-200 bg-white py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:grid-cols-12 md:gap-16 md:px-12">
          <div className="md:col-span-5">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — The #1 Reason Customers Don't Return
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              A neglected restroom
              <br />
              <span className="text-slate-400">is a lost customer.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
            <p>
              Studies on customer experience consistently show that restroom
              cleanliness is one of the top factors people use to judge the
              overall cleanliness — and trustworthiness — of a business.
              Whether you run a restaurant in downtown Wilmington, a gas
              station off Market Street, a bar near Wrightsville Beach, or a
              retail shop in Mayfaire, your restrooms are a direct reflection
              of your brand.
            </p>
            <p>
              Customers assume that if the bathroom is neglected, the kitchen,
              stockroom, or back office probably is too. This is especially
              true here in the Wilmington area, where tourism and foot traffic
              spike seasonally. Beach visitors heading to Wrightsville Beach,
              Carolina Beach, and Kure Beach are constantly stopping into
              local businesses — and they remember which ones had clean
              restrooms and which ones didn't.
            </p>
          </div>
        </div>
      </section>

      {/* INFOGRAPHIC */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <div className="mb-10 text-center">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — The Full Breakdown
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Why professional restroom cleaning matters.
            </h2>
            <p className="mt-4 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Save or share — the complete Deep Clean overview for Wilmington
              businesses: who we serve, our 7 Layers of Clean, service
              options, and coverage across the Cape Fear region.
            </p>
          </div>
          <figure className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
            <img
              src={INFOGRAPHIC_IMG}
              alt="Why Professional Restroom Cleaning Matters for Wilmington, NC Businesses — Deep Clean Restroom Services infographic: The #1 Reason Customers Don't Return, Who We Serve, Our 7 Layers of Clean, Flexible Service Options, Why Local Matters, and Wilmington-area service coverage map"
              data-testid="pro-infographic"
              className="w-full"
              loading="lazy"
            />
            <figcaption className="bg-slate-950 px-5 py-3 text-center font-body text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">
              Because Clean Restrooms Matter · Deep Clean
            </figcaption>
          </figure>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — Who We Serve
              </p>
              <h2
                data-testid="pro-serve-heading"
                className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
              >
                Every commercial space
                <br />
                <span className="text-blue-600">the public walks into.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                If your customers or employees use your restrooms, professional
                cleaning isn't a luxury. It's a necessity. Here's who we work
                with across the Cape Fear region.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            {WHO_WE_SERVE.map((s, i) => (
              <div
                key={s.title}
                data-testid={`pro-serve-${i}`}
                className="group bg-white p-8 transition-colors hover:bg-slate-950"
              >
                <span className="grid h-12 w-12 place-items-center rounded-md bg-blue-600/10 text-blue-600 transition-colors group-hover:bg-cyan-300/15 group-hover:text-cyan-300">
                  <s.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-slate-900 transition-colors group-hover:text-white lg:text-xl">
                  {s.title}
                </h3>
                <p className="mt-3 font-body text-sm font-medium leading-relaxed text-slate-600 transition-colors group-hover:text-white/80">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 LAYERS */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — The 7 Layers of Clean™
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Deeply clean —
              <br />
              <span className="text-slate-400">not just surface-tidy.</span>
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Most janitorial companies treat restroom cleaning as an
              afterthought. We built our entire company around it.
            </p>
          </div>

          <ol
            data-testid="pro-layers-list"
            className="overflow-hidden rounded-md border border-slate-200 bg-white"
          >
            {SEVEN_LAYERS.map((s, i) => (
              <li
                key={s.title}
                data-testid={`pro-layer-${i}`}
                className="group grid grid-cols-[auto,auto,1fr,auto] items-start gap-5 border-b border-slate-200 p-6 transition-colors last:border-b-0 hover:bg-slate-50 md:p-8"
              >
                <span className="mt-1 font-heading text-sm font-bold tabular-nums text-slate-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="grid h-11 w-11 place-items-center rounded-md border border-slate-200 bg-white text-blue-600 transition-colors group-hover:border-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                  <s.icon size={20} strokeWidth={1.6} />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 md:text-xl">
                    {s.title}
                  </h3>
                  <p className="mt-1.5 font-body text-sm font-medium leading-relaxed text-slate-600">
                    {s.desc}
                  </p>
                </div>
                <span
                  className="mt-1 inline-flex h-6 items-center gap-1 rounded-full bg-blue-50 px-2.5 font-body text-[10px] font-bold uppercase tracking-wider text-blue-700"
                  data-testid={`pro-layer-included-${i}`}
                >
                  <CheckCircle2 size={12} strokeWidth={3} />
                  Included
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SERVICE OPTIONS */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Flexible Service Options
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              One-time or recurring — <span className="text-blue-600">your call.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div
              data-testid="pro-option-onetime"
              className="rounded-md border border-slate-200 bg-white p-8 transition-colors hover:border-blue-600 md:p-10"
            >
              <span className="grid h-12 w-12 place-items-center rounded-md bg-blue-600/10 text-blue-600">
                <CalendarClock size={22} strokeWidth={1.6} />
              </span>
              <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
                One-Time Deep Cleans
              </h3>
              <p className="mt-3 font-body text-base font-medium leading-relaxed text-slate-600">
                Perfect for a grand opening, a health inspection prep, a
                special event, or simply resetting a restroom that's fallen
                behind.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Grand opening prep",
                  "Health-inspection ready",
                  "Special events & seasonal reset",
                  "One-time reset of a neglected restroom",
                ].map((x) => (
                  <li
                    key={x}
                    className="flex items-start gap-3 font-body text-sm font-semibold text-slate-800 md:text-base"
                  >
                    <CheckCircle2
                      size={18}
                      strokeWidth={2}
                      className="mt-0.5 shrink-0 text-blue-600"
                    />
                    {x}
                  </li>
                ))}
              </ul>
            </div>

            <div
              data-testid="pro-option-recurring"
              className="rounded-md border-2 border-blue-600 bg-slate-950 p-8 text-white md:p-10"
            >
              <span className="grid h-12 w-12 place-items-center rounded-md bg-cyan-300/15 text-cyan-300">
                <Repeat size={22} strokeWidth={1.6} />
              </span>
              <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight md:text-3xl">
                Recurring Maintenance
              </h3>
              <p className="mt-3 font-body text-base font-medium leading-relaxed text-slate-300">
                Weekly, bi-weekly, or monthly visits that keep your restrooms
                consistently clean without your staff having to worry about
                it.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Weekly, bi-weekly, or monthly cadence",
                  "Consistent standard year-round",
                  "Priority scheduling",
                  "Restocking & supply-check included",
                ].map((x) => (
                  <li
                    key={x}
                    className="flex items-start gap-3 font-body text-sm font-semibold md:text-base"
                  >
                    <CheckCircle2
                      size={18}
                      strokeWidth={2}
                      className="mt-0.5 shrink-0 text-cyan-300"
                    />
                    {x}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-8 max-w-3xl font-body text-sm font-medium leading-relaxed text-slate-600 md:text-base">
            Many of our Wilmington-area clients start with a one-time deep
            clean to see the difference firsthand, then move into a recurring
            plan to maintain that standard year-round.
          </p>
        </div>
      </section>

      {/* WHY LOCAL */}
      <section className="border-y border-slate-200 bg-slate-950 py-20 text-white md:py-28">
        <div
          className="pointer-events-none absolute inset-x-0 -mt-20 h-[200px] opacity-30"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(60% 60% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(40% 40% at 0% 100%, rgba(0,85,255,0.25), transparent)",
          }}
        />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              — Why Local Matters
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Locally owned.
              <br />
              <span className="text-slate-400">Locally accountable.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 font-body text-base font-medium leading-relaxed text-slate-300 md:text-lg">
            <p>
              Deep Clean Restroom Services isn't a national franchise juggling
              hundreds of accounts across the state. We're based right here in
              the Wilmington, NC area, which means we understand the unique
              challenges local businesses face — from the sandy, high-traffic
              conditions near Wrightsville Beach and Carolina Beach to the
              steady weekday office traffic downtown and along Military
              Cutoff Road.
            </p>
            <p>
              We show up on time, we know the area, and we treat every
              restroom like it's a reflection of our own name.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Service Areas
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Serving the greater Wilmington, NC area.
            </h2>
            <p className="mt-4 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Commercial restroom cleaning and janitorial services across New
              Hanover, Pender, and Brunswick counties.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {SERVICE_AREAS.map((area, i) => (
              <div
                key={area}
                data-testid={`pro-area-${i}`}
                className="group flex items-center gap-2.5 rounded-md border border-slate-200 bg-white p-3 transition-colors hover:border-blue-600"
              >
                <MapPin
                  size={14}
                  strokeWidth={2}
                  className="shrink-0 text-blue-600"
                />
                <span className="font-body text-[13px] font-semibold text-slate-800">
                  {area}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — FAQ
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Common Questions.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Other questions about commercial restroom cleaning in Wilmington
              or the Cape Fear region? Call{" "}
              <a
                href="tel:9102369971"
                className="font-bold text-blue-600 hover:underline"
              >
                910-236-9971
              </a>
              .
            </p>
          </div>
          <div className="lg:col-span-8">
            {FAQS.map((f, i) => (
              <Faq key={f.q} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-slate-950 py-20 text-white md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(50% 40% at 0% 100%, rgba(0,85,255,0.40), transparent)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 md:px-12 lg:flex-row lg:items-center">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              — Wilmington, NC · Cape Fear Region
            </p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl lg:text-5xl">
              Ready for a restroom your customers won't complain about?
            </h2>
            <p className="mt-4 max-w-xl font-body text-sm font-medium text-slate-300 md:text-base">
              Per-unit pricing based on the number of restrooms and fixtures at
              your location — you only pay for what you need.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="pro-cta-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Get a Free Quote
              <ArrowRight
                size={18}
                strokeWidth={2.2}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="pro-cta-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              910-236-9971
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ProfessionalRestroomCleaningPage;
